import CloseIcon from '@mui/icons-material/Close';
import { Box, DialogContent, DialogTitle, Drawer, IconButton, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CustomDialog, dialogStyles } from './SelectTreatmentDialog.styles';
import { treatmentCodeToReasonCode } from '@src/constants';
import { useSchedulingStore } from '@src/store';
import { pushToDataLayer } from '@src/utils/pushToDataLayer';
import { ZenotiService } from 'types/service';
interface ISelectTreatmentDialogProps {
  open: boolean;
  handleClose: () => void;
  treatments: ZenotiService[];
  dataTestId?: string;
}
interface ITreatmentsModalBodyProps {
  treatments: ZenotiService[];
  handleClose: () => void;
  handleSelectTreatment: (treatment: ZenotiService) => void;
  selectedTreatmentId?: string;
  selectedTreatmentName?: string;
  selectedTreatmentCode?: string;
}
const TreatmentsModalBody = ({
  handleClose,
  treatments,
  handleSelectTreatment,
  selectedTreatmentId
}: ITreatmentsModalBodyProps) => {
  return <>
			<DialogTitle sx={dialogStyles.dialogTitle} data-test-id="dialog-title" data-sentry-element="DialogTitle" data-sentry-source-file="SelectTreatmentDialog.tsx">
				<Typography variant="bodyLargeBold" data-sentry-element="Typography" data-sentry-source-file="SelectTreatmentDialog.tsx">Select a treatment</Typography>
				<IconButton aria-label="close" onClick={handleClose} sx={dialogStyles.closeButton} data-test-id="close-button" data-sentry-element="IconButton" data-sentry-source-file="SelectTreatmentDialog.tsx">
					<CloseIcon data-sentry-element="CloseIcon" data-sentry-source-file="SelectTreatmentDialog.tsx" />
				</IconButton>
			</DialogTitle>
			<DialogContent data-sentry-element="DialogContent" data-sentry-source-file="SelectTreatmentDialog.tsx">
				{treatments?.map(treatment => {
        return <Box key={treatment.code} onClick={() => handleSelectTreatment(treatment)} sx={dialogStyles.treatmentBox(selectedTreatmentId === treatment.id)} data-test-id={`treatment-box-${treatment.code}`}>
							<Typography variant="bodySmallBook">
								{treatment.catalog_info.display_name.toUpperCase()}
							</Typography>
						</Box>;
      })}
			</DialogContent>
		</>;
};
const SelectTreatmentDialog = ({
  open,
  handleClose,
  treatments,
  dataTestId = 'select-treatment-dialog'
}: ISelectTreatmentDialogProps) => {
  const {
    setSelectedTreatmentId,
    setSelectedTreatmentName,
    setSelectedTreatmentCode,
    setTreatmentUpdated,
    selectedTreatmentId,
    selectedTreatmentName,
    selectedTreatmentCode,
    selectedLocation
  } = useSchedulingStore(state => state);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const segmentType = useSchedulingStore().returningGuest ? 1 : 0;
  const handleSelectTreatment = (treatment: ZenotiService) => {
    // Braze event: Step 02 - Treatment Selected
    import('@src/utils/braze-exports').then(({
      getUser
    }) => {
      getUser()?.setCustomUserAttribute('current-treatment-selected', treatment.catalog_info.display_name);
      getUser()?.setCustomUserAttribute('current-location-selected', selectedLocation?.name ?? '');
    });
    const dataForDataLayer = {
      event: 'appointment_step',
      stepName: 'select_a_treatment',
      treatmentId: treatment.id,
      treatmentName: treatment.catalog_info.display_name,
      reasonCode: treatmentCodeToReasonCode[treatment.code],
      segmentType: segmentType
    };
    pushToDataLayer(dataForDataLayer);
    setTreatmentUpdated(true);
    setSelectedTreatmentId(treatment.id);
    setSelectedTreatmentName(treatment.catalog_info.display_name);
    setSelectedTreatmentCode(treatment.code);
    handleClose();
  };
  return isMobile ? <Drawer anchor="bottom" open={open} onClose={handleClose} data-test-id={dataTestId} data-sentry-element="Drawer" data-sentry-component="SelectTreatmentDialog" data-sentry-source-file="SelectTreatmentDialog.tsx">
			<TreatmentsModalBody handleClose={handleClose} treatments={treatments} handleSelectTreatment={handleSelectTreatment} selectedTreatmentId={selectedTreatmentId} selectedTreatmentName={selectedTreatmentName} selectedTreatmentCode={selectedTreatmentCode} data-sentry-element="TreatmentsModalBody" data-sentry-source-file="SelectTreatmentDialog.tsx" />
		</Drawer> : <CustomDialog open={open} onClose={handleClose} data-test-id={dataTestId} data-sentry-element="CustomDialog" data-sentry-component="SelectTreatmentDialog" data-sentry-source-file="SelectTreatmentDialog.tsx">
			<TreatmentsModalBody handleClose={handleClose} treatments={treatments} handleSelectTreatment={handleSelectTreatment} selectedTreatmentId={selectedTreatmentId} selectedTreatmentName={selectedTreatmentName} selectedTreatmentCode={selectedTreatmentCode} data-sentry-element="TreatmentsModalBody" data-sentry-source-file="SelectTreatmentDialog.tsx" />
		</CustomDialog>;
};
export default SelectTreatmentDialog;